// Libs
import React from 'react';
import { createSlice, Dispatch } from '@reduxjs/toolkit';
import { RootState } from 'redux/store';
import { Action } from 'redux/types/Action';
import { DialogService, SnackbarService, Typography, defaultTheme as theme } from 'ui-components';
// App
import { ProductDetails, ShopProductSummary } from 'app-shared/lib/api/catalog/product';
import { ShopDetails } from 'app-shared/lib/api/catalog/shop';
import { AddProductAction, BagSliceState, ExistingProductAction } from './bag.slice.interfaces';
import { checkIfCanOrderProduct } from './bag.slice.utils';
import { CallbackSnackbar } from 'app-shared/lib/components/feedback/callback-snackbar';
import { ORDER_TYPE } from 'app-shared/lib/api/shared/order.interfaces';
import { reducers } from './bag.slice.reducers';
import { extraReducers } from './bag.slice.extra-reducers';
import { initialState } from './bag.slice.constants';
import { SelectableProductOptionFormData } from 'app-shared/lib/fragments/product/options-groups';

const bagSlice = createSlice({
  name: 'bag',
  initialState,
  reducers,
  extraReducers,
});

// Product

export const addProductToBag = (
  product: ProductDetails | ShopProductSummary,
  shop: ShopDetails,
  selectedOptions?: SelectableProductOptionFormData[],
  isAiSuggestion?: boolean
) => async (dispatch: Dispatch<Action<AddProductAction>>, getState: () => RootState): Promise<boolean> => {
  const stateBefore: RootState = getState();
  const currentShopId = stateBefore.bag.order && stateBefore.bag.order.shop.id;

  const canOrderProduct = await checkIfCanOrderProduct(stateBefore.bag, product.id);

  if (!canOrderProduct) return false;

  if (currentShopId && currentShopId != shop.id) {
    const response = await DialogService.askYesNoQuestion({
      title: 'Voulez-vous créer une nouvelle commande ?',
      body: (
        <Typography>
          Des produits d&apos;un autre restaurant sont déja dans la commande. <br />
          Voulez-vous créer une nouvelle commande avec le produit que vous venez de sélectionner ?
        </Typography>
      ),
      theme,
    });

    if (response == 'yes') {
      dispatch(emptyAll());
    } else {
      return;
    }
  }

  dispatch(
    addProduct({
      product,
      shop,
      selectedOptions,
      isAiSuggestion,
    })
  );

  SnackbarService.showSnackbar((onClose) => <CallbackSnackbar message="Ajouté à la commande" onClose={onClose} />);

  return true;
};

export const removeProductFromBag = (productIndex: number) => (
  dispatch: Dispatch<Action<ExistingProductAction>>
): void => {
  dispatch(
    removeProduct({
      productIndex,
    })
  );
};

export const increaseProductQuantityInBag = (productIndex: number, productId: number) => async (
  dispatch: Dispatch<Action<ExistingProductAction>>,
  getState: () => RootState
): Promise<boolean> => {
  const stateBefore: RootState = getState();

  const canOrderProduct = await checkIfCanOrderProduct(stateBefore.bag, productId);

  if (!canOrderProduct) return false;

  dispatch(
    increaseProductQuantity({
      productIndex,
    })
  );

  return true;
};

export const decreaseProductQuantityInBag = (productIndex: number) => (
  dispatch: Dispatch<Action<ExistingProductAction>>
): void => {
  dispatch(
    decreaseProductQuantity({
      productIndex,
    })
  );
};

// Order

export const setMeetingDateForOrderInBag = (meetingStartDate: Date, meetingEndDate: Date) => (
  dispatch: Dispatch<Action<{ meetingStartDate: Date; meetingEndDate: Date }>>
): void => {
  dispatch(setMeetingDateForOrder({ meetingStartDate, meetingEndDate }));
};

export const setTypeForOrderInBag = (type: ORDER_TYPE) => (dispatch: Dispatch<Action<{ type: ORDER_TYPE }>>): void => {
  dispatch(setTypeForOrder({ type }));
};

// Global

export const emptyBag = () => (dispatch: Dispatch<Action<void>>): void => {
  dispatch(emptyAll());
};

export const {
  addProduct,
  removeProduct,
  increaseProductQuantity,
  decreaseProductQuantity,
  setMeetingDateForOrder,
  setTypeForOrder,
  setUserSelectedSuggestedProducts,
  emptyAll,
} = bagSlice.actions;

export { getShop, getProduct } from './bag.slice.extra-reducers';

export type BagState = Readonly<BagSliceState>;
export type { Order, Product } from './bag.slice.interfaces';

export default bagSlice.reducer;
